import { makeAutoObservable, runInAction, toJS } from 'mobx';
import {
  DependentsChoice,
  ProductInfo,
  ProductLineInterestChange,
  RateStatistic,
  Subscriber,
  UpdatedPlanInfo,
} from 'types/electionService';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { updateSubscriber } from 'api/operations/electionService';
import {
  getChoiceByProductLineId,
  getInterestedProductLineByProductLineId,
} from 'global/utils/election/productLineElection';
import {
  buildChoicesForSubscriber,
  buildInterestedProductLinesForSubscriber,
} from 'global/utils/election/productLineInitialOptions';
import { SubscriberElectionPresenter, SubscriberElectionSource } from './types';
import { dependentIdsFromSubscriber, mapSubscriberToElectionPresenter } from './utils';

export interface SubscriberElectionsDelegateParams {
  election: SubscriberElectionPresenter;
  dependentIds: string[];
  periodId: string;
  products: ProductInfo[];
  rateStats: RateStatistic | null;
  domainProductLineIds: ProductLineId[];
  refreshSubscriber: () => Promise<Subscriber>;
  refreshStatistics: () => Promise<void>;
}

/** Election writes and in-memory defaults for one subscriber. Does not fetch. */
export default class SubscriberElectionsDelegate {
  private _election: SubscriberElectionPresenter;
  private _periodId: string;
  private _products: ProductInfo[];
  private _rateStats: RateStatistic | null;
  private _domainProductLineIds: ProductLineId[];
  private readonly _refreshSubscriber: () => Promise<Subscriber>;
  private readonly _refreshStatistics: () => Promise<void>;

  /** Constructs a delegate from a BE `Subscriber` (maps at the entrance). */
  public static fromSubscriber(
    subscriber: SubscriberElectionSource,
    params: Omit<SubscriberElectionsDelegateParams, 'election' | 'dependentIds'>,
  ): SubscriberElectionsDelegate {
    return new SubscriberElectionsDelegate({
      ...params,
      election: mapSubscriberToElectionPresenter(subscriber),
      dependentIds: dependentIdsFromSubscriber(subscriber),
    });
  }

  public get electionPresenter(): SubscriberElectionPresenter {
    return this._election;
  }

  public async syncSelectedPlan(selectedPlanInfo: UpdatedPlanInfo): Promise<void> {
    const { productLineId, selectedPlanName, selectedPlanId } = selectedPlanInfo;
    const refreshStatistics = this._refreshStatistics;

    const currentChoice = this._election.choice.find((choiceItem) => choiceItem.productLineId === productLineId);

    if (currentChoice && selectedPlanName) {
      currentChoice.productId = selectedPlanId;
      currentChoice.productName = selectedPlanName;
    }
    await this._persistAndRefresh();
    await refreshStatistics();
  }

  public async syncDependents(dependentsChoice: DependentsChoice): Promise<void> {
    const refreshStatistics = this._refreshStatistics;
    const currentChoice = getChoiceByProductLineId(this._election.choice, dependentsChoice.productLineId) || null;

    if (!currentChoice) {
      return;
    }

    currentChoice.dependentIds = dependentsChoice.dependentIds;
    await this._persistAndRefresh();
    await refreshStatistics();
  }

  public async syncDependentsInInterestedProductLines(dependentsChoice: DependentsChoice): Promise<void> {
    const interestedProductLine = getInterestedProductLineByProductLineId(
      this._election.interestedProductLines,
      dependentsChoice.productLineId,
    );
    if (!interestedProductLine) {
      return;
    }

    interestedProductLine.dependentIds = dependentsChoice.dependentIds;
    await this._persistAndRefresh();
  }

  public async syncProductLineInterest(change: ProductLineInterestChange): Promise<void> {
    const updatedLine = this._election.interestedProductLines.find(
      (item) => item.productLineId === change.productLineId,
    );
    if (updatedLine) {
      updatedLine.value = change.selection;
    } else {
      this._election.interestedProductLines.push({
        productLineId: change.productLineId,
        dependentIds: [],
        value: change.selection,
      });
    }
    await this._persistAndRefresh();
  }

  public async syncClassAndResetProductSelection(className: string): Promise<void> {
    const refreshStatistics = this._refreshStatistics;
    this._election.employeeClass = className;
    this._election.choice = this._election.choice.map((choice) => {
      return { ...choice, productId: '', productName: '' };
    });
    await this._persistAndRefresh();
    await refreshStatistics();
  }

  /**
   * Re-applies in-memory election defaults (choices + interested product lines; no BE save).
   *
   * Defaults are built once in the constructor. Later dependent-list changes live on basic-info,
   * so missing election rows can become stale until this runs with the current dependent ids.
   * BE-saved rows still win; only missing lines get fresh defaults.
   */
  public syncElectionDefaults(dependentIds: string[]): void {
    this._election = this._withElectionDefaults(this._election, dependentIds);
  }

  private _electionWriteFields(): Pick<
    Subscriber,
    'id' | 'employeeClass' | 'choice' | 'interestedProductLines' | 'productLineToCarrier'
  > {
    return {
      id: this._election.id,
      employeeClass: this._election.employeeClass,
      choice: toJS(this._election.choice),
      interestedProductLines: toJS(this._election.interestedProductLines),
      productLineToCarrier: toJS(this._election.productLineToCarrier),
    };
  }

  private async _persistAndRefresh(): Promise<void> {
    const refreshSubscriber = this._refreshSubscriber;
    const periodId = this._periodId;
    const electionFields = this._electionWriteFields();
    const latest = await refreshSubscriber();
    await updateSubscriber(periodId, {
      ...latest,
      ...electionFields,
    });
    const refreshed = await refreshSubscriber();
    runInAction(() => {
      this._election = mapSubscriberToElectionPresenter(refreshed);
    });
  }

  private _withElectionDefaults(
    election: SubscriberElectionPresenter,
    dependentIds: string[],
  ): SubscriberElectionPresenter {
    return {
      ...election,
      choice: buildChoicesForSubscriber(election.choice, dependentIds, this._products, this._rateStats),
      interestedProductLines: buildInterestedProductLinesForSubscriber(
        election.interestedProductLines,
        dependentIds,
        this._domainProductLineIds,
      ),
    };
  }

  constructor({
    election,
    dependentIds,
    periodId,
    products,
    rateStats,
    domainProductLineIds,
    refreshSubscriber,
    refreshStatistics,
  }: SubscriberElectionsDelegateParams) {
    this._periodId = periodId;
    this._products = products;
    this._rateStats = rateStats;
    this._domainProductLineIds = domainProductLineIds;
    this._refreshSubscriber = refreshSubscriber;
    this._refreshStatistics = refreshStatistics;
    this._election = this._withElectionDefaults(election, dependentIds);

    makeAutoObservable(this, {}, { autoBind: true });
  }
}
